import { KdsActionId, toActionIds } from '../Types/KdsAuthorization'

// The step-up token carries the operator's granted actions under the standard `permissions` claim
// (emitted by Auth's KdsUserClaimsService). Isolated here so a rename touches one place. Never
// verifies the token; decode-only, for local preflight.
const PERMISSIONS = 'permissions'

// Only the permissions claim is read, and its values are ASCII action-id strings, so this doesn't
// need a UTF-8-safe decode -- a plain atob+JSON.parse round-trip is enough.
function decodeJwtPayload(token: string): Record<string, unknown> | undefined {
  try {
    const payload = token.split('.')[1]
    if (!payload) return undefined

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    return JSON.parse(atob(padded))
  } catch {
    return undefined
  }
}

/**
 * Best-effort only: the backend is authoritative for what an action can do.
 *
 * `undefined` is reserved for a missing/unreadable claim, which the coordinator treats as "let the
 * backend decide". A present-but-empty (or all-unrecognized) claim returns `[]` so the coordinator's
 * preflight rejects the action locally instead of issuing a request that can only 403.
 */
export function extractAllowedActions(token: string): KdsActionId[] | undefined {
  const raw = decodeJwtPayload(token)?.[PERMISSIONS]

  if (typeof raw === 'string') {
    return toActionIds([raw])
  }

  if (!Array.isArray(raw) || !raw.every((value): value is string => typeof value === 'string')) {
    return undefined
  }

  return toActionIds(raw)
}
